// src/components/ErrorState.js
import React from 'react';
import {
    Box,
    Typography,
    Button,
    Paper,
    useTheme,
    Avatar,
    Zoom,
    Fade
} from '@mui/material';
import {
    Error as ErrorIcon,
    Refresh,
    LunchDining,
    Restaurant,
    Warning
} from '@mui/icons-material';

const ErrorState = ({ 
    error, 
    onRetry, 
    title = "Oops! Something went wrong",
    icon = null,
    fullPage = false,
    showRetry = true,
    retryText = "Try Again"
}) => {
    const theme = useTheme();

    // Using bag color theme
    const errorColor = '#8B4513'; // Saddle brown
    const errorLight = '#A0522D'; // Sienna

    // Default lunch welfare icon
    const getIcon = () => {
        if (icon) return icon;
        return <LunchDining sx={{ fontSize: 80 }} />;
    };

    // Get error message
    const getErrorMessage = () => {
        if (error) return error;
        return "An unexpected error occurred. Please try again.";
    };

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: fullPage ? '100vh' : '400px',
                width: '100%',
                gap: 3,
                p: 4,
                position: 'relative'
            }}
        >
            <Zoom in={true}>
                <Box sx={{ position: 'relative' }}>
                    {/* Error icon */}
                    <Box
                        sx={{
                            animation: 'shake 0.5s ease-in-out',
                            '@keyframes shake': {
                                '0%, 100%': { transform: 'translateX(0)' },
                                '10%, 30%, 50%, 70%, 90%': { transform: 'translateX(-5px)' },
                                '20%, 40%, 60%, 80%': { transform: 'translateX(5px)' },
                            },
                        }}
                    >
                        <Avatar
                            sx={{
                                width: 120,
                                height: 120,
                                bgcolor: `${errorColor}20`,
                                color: errorColor,
                                boxShadow: `0 8px 20px ${errorColor}40`
                            }}
                        >
                            {getIcon()}
                        </Avatar>
                    </Box>
                    
                    {/* Pulsing error badge */}
                    <Box
                        sx={{
                            position: 'absolute',
                            top: 0,
                            right: 0,
                            width: 30,
                            height: 30,
                            borderRadius: '50%',
                            backgroundColor: theme.palette.error.main,
                            color: 'white',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: 20,
                            fontWeight: 'bold',
                            animation: 'pulse-error 1.5s ease-in-out infinite',
                            '@keyframes pulse-error': {
                                '0%': { transform: 'scale(1)', boxShadow: `0 0 0 0 ${theme.palette.error.main}70` },
                                '70%': { transform: 'scale(1.1)', boxShadow: `0 0 0 10px ${theme.palette.error.main}00` },
                                '100%': { transform: 'scale(1)', boxShadow: `0 0 0 0 ${theme.palette.error.main}00` },
                            },
                        }}
                    >
                        !
                    </Box>
                </Box>
            </Zoom>
            
            <Fade in={true} timeout={800}>
                <Box sx={{ textAlign: 'center', maxWidth: 500 }}>
                    <Typography 
                        variant="h4" 
                        sx={{ 
                            color: errorColor,
                            fontWeight: 600,
                            mb: 2
                        }}
                    >
                        {title}
                    </Typography>
                    
                    <Paper
                        elevation={0}
                        sx={{
                            p: 3,
                            bgcolor: `${errorColor}08`,
                            borderRadius: 3,
                            border: `1px solid ${errorColor}30`,
                            mb: 3
                        }}
                    >
                        <Typography 
                            variant="body1" 
                            sx={{ 
                                color: errorColor,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                justifyContent: 'center',
                                wordBreak: 'break-word'
                            }}
                        >
                            <ErrorIcon sx={{ color: errorColor }} />
                            {getErrorMessage()}
                        </Typography>
                    </Paper>
                    
                    {showRetry && onRetry && (
                        <Button
                            variant="contained"
                            size="large"
                            startIcon={<Refresh />}
                            onClick={onRetry}
                            sx={{
                                borderRadius: 2,
                                px: 4,
                                py: 1.5,
                                textTransform: 'none',
                                fontWeight: 600,
                                background: `linear-gradient(45deg, ${errorColor} 30%, ${errorLight} 90%)`,
                                boxShadow: `0 8px 16px ${errorColor}40`,
                                '&:hover': {
                                    transform: 'translateY(-2px)',
                                    boxShadow: `0 12px 20px ${errorColor}60`,
                                },
                                transition: 'all 0.3s ease'
                            }}
                        >
                            {retryText}
                        </Button>
                    )}
                </Box>
            </Fade>
            
            {/* Decorative background elements */}
            <Box
                sx={{
                    position: 'absolute',
                    bottom: 20,
                    right: 20,
                    opacity: 0.05,
                    zIndex: -1
                }}
            >
                <LunchDining sx={{ fontSize: 200 }} />
            </Box>
            
            <Box
                sx={{
                    position: 'absolute',
                    top: 20,
                    left: 20,
                    opacity: 0.03,
                    zIndex: -1
                }}
            >
                <Restaurant sx={{ fontSize: 150 }} />
            </Box>
        </Box>
    );
};

export default ErrorState;